import React, { useState, useRef, useEffect, useCallback } from 'react';
import { paymentVerificationAPI } from '../services/api.ts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MobileDatePicker from '../components/MobileDatePicker.tsx';

interface ServiceOrder {
  id: number;
  serviceOrderNo: string;
  invoiceNo: string;
  serviceOrderDate: string;
  customerName: string;
  customerContactNo: string;
  customerAddress: string;
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;
}

interface SelectedServiceOrder extends ServiceOrder {
  allocatedAmount: number;
}

interface PaymentData {
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  bankRefNo: string;
  notes: string;
}

const PaymentVerification: React.FC = () => {
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [ocrParsedData, setOcrParsedData] = useState<PaymentData | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentData>({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    bankRefNo: '',
    notes: ''
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ServiceOrder[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedServiceOrders, setSelectedServiceOrders] = useState<SelectedServiceOrder[]>([]);
  const [showServiceOrderModal, setShowServiceOrderModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compress image before upload
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;
          const maxHeight = 1920;
          const quality = 0.8;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle receipt file selection and OCR processing
  const handleReceiptChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      
      // Compress image before processing
      let processedFile = originalFile;
      try {
        processedFile = await compressImage(originalFile);
        if (processedFile.size > 1024 * 1024) {
          toast.warning(`Image size is ${Math.round(processedFile.size / 1024)}KB. OCR may fail if size exceeds 1024KB.`);
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        toast.warning('Failed to compress image, using original file');
      }

      const previewUrl = URL.createObjectURL(processedFile);
      setReceiptFile(processedFile);
      setReceiptPreview(previewUrl);
      setIsProcessingOCR(true);
      setOcrParsedData(null);

      // Call API to extract payment data from receipt using OCR
      try {
        const result = await paymentVerificationAPI.extractReceiptData(processedFile);
        
        setIsProcessingOCR(false);
        
        if (result.parsed) {
          setOcrParsedData(result.parsed);
          
          // Auto-populate payment data with parsed values
          const updates: Partial<PaymentData> = {};
          
          if (result.parsed.amount) {
            updates.amount = result.parsed.amount;
          }
          
          if (result.parsed.bankRefNo) {
            updates.bankRefNo = result.parsed.bankRefNo;
          }
          
          if (result.parsed.paymentDate) {
            // Parse date string directly to avoid timezone conversion issues
            // Backend sends "2026-03-13T00:00:00", we need to extract just the date part
            const dateStr = result.parsed.paymentDate;
            // If it's already in YYYY-MM-DD format, use it directly
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              updates.paymentDate = dateStr;
            } else {
              // If it's in ISO format with time, extract just the date part
              updates.paymentDate = dateStr.split('T')[0];
            }
          }
          
          if (result.parsed.paymentMethod) {
            updates.paymentMethod = result.parsed.paymentMethod;
          }
          
          if (result.parsed.notes) {
            updates.notes = result.parsed.notes;
          }
          
          setPaymentData(prev => ({
            ...prev,
            ...updates
          }));
          
          if (result.parsed.amount) {
            toast.success(`Receipt extracted! Amount detected: RM ${result.parsed.amount.toFixed(2)}`);
          } else {
            toast.success('Receipt text extracted successfully. Please verify and enter details manually.');
          }
        } else {
          toast.info('Receipt processed. Please enter payment details manually.');
        }
      } catch (error: any) {
        console.error('OCR Error:', error);
        setIsProcessingOCR(false);
        toast.error(error.message || 'Failed to extract receipt data. Please enter details manually.');
      }
    }
  };

  // Search service orders
  const handleSearchServiceOrders = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setIsSearching(true);
    try {
      const response = await paymentVerificationAPI.searchServiceOrders(searchQuery.trim());
      setSearchResults(response.data || []);
      if (response.data && response.data.length === 0) {
        toast.info('No service orders found');
      }
    } catch (error: any) {
      console.error('Search Error:', error);
      toast.error(error.message || 'Failed to search service orders');
    } finally {
      setIsSearching(false);
    }
  };

  // Add service order to selection
  const handleAddServiceOrder = (serviceOrder: ServiceOrder) => {
    const remainingBalance = serviceOrder.remainingBalance;
    const currentTotalAllocated = getTotalAllocated();
    const availablePaymentAmount = paymentData.amount - currentTotalAllocated;
    
    // Allocate the full remaining balance, but ensure total doesn't exceed payment amount
    const defaultAmount = remainingBalance > 0 
      ? Math.min(remainingBalance, availablePaymentAmount) 
      : 0;
    
    const newSelection: SelectedServiceOrder = {
      ...serviceOrder,
      allocatedAmount: defaultAmount
    };
    
    setSelectedServiceOrders(prev => [...prev, newSelection]);
    setSearchQuery('');
    setSearchResults([]);
    
    if (defaultAmount < remainingBalance) {
      toast.warning(`Service order added. Allocated RM ${defaultAmount.toFixed(2)} (full balance: RM ${remainingBalance.toFixed(2)}) to stay within payment amount.`);
    } else {
      toast.success('Service order added with full remaining balance allocated');
    }
  };

  // Remove service order from selection
  const handleRemoveServiceOrder = (id: number) => {
    setSelectedServiceOrders(prev => prev.filter(so => so.id !== id));
  };

  // Update allocated amount for a service order
  const handleUpdateAllocatedAmount = (id: number, amount: number) => {
    // Ensure amount is not negative
    if (amount < 0) {
      amount = 0;
    }
    
    setSelectedServiceOrders(prev => {
      const serviceOrder = prev.find(so => so.id === id);
      if (!serviceOrder) return prev;
      
      // Ensure allocated amount doesn't exceed remaining balance
      const maxAmount = serviceOrder.remainingBalance;
      if (amount > maxAmount) {
        toast.warning(`Amount cannot exceed remaining balance (RM ${maxAmount.toFixed(2)})`);
        amount = maxAmount;
      }
      
      // Calculate what the new total would be
      const currentTotal = prev.reduce((sum, so) => sum + (so.id === id ? 0 : (so.allocatedAmount || 0)), 0);
      const newTotal = currentTotal + amount;
      
      // Ensure total allocated doesn't exceed payment amount
      if (newTotal > paymentData.amount) {
        const maxAllowed = paymentData.amount - currentTotal;
        toast.warning(`Total allocated amount cannot exceed payment amount. Maximum allowed: RM ${maxAllowed.toFixed(2)}`);
        amount = Math.max(0, maxAllowed);
      }
      
      return prev.map(so => 
        so.id === id ? { ...so, allocatedAmount: amount } : so
      );
    });
  };

  // Calculate total allocated amount
  const getTotalAllocated = () => {
    return selectedServiceOrders.reduce((sum, so) => sum + (so.allocatedAmount || 0), 0);
  };

  // Calculate remaining unallocated amount
  const getRemainingAmount = () => {
    return paymentData.amount - getTotalAllocated();
  };

  // Submit payment verification
  const handleSubmitPayment = async () => {
    if (!paymentData.amount || paymentData.amount <= 0) {
      toast.error('Please enter payment amount');
      return;
    }

    if (!paymentData.paymentDate) {
      toast.error('Please select payment date');
      return;
    }

    if (selectedServiceOrders.length === 0) {
      toast.error('Please select at least one service order');
      return;
    }

    const totalAllocated = getTotalAllocated();
    if (Math.abs(totalAllocated - paymentData.amount) > 0.01) {
      toast.error(`Total allocated amount (RM ${totalAllocated.toFixed(2)}) must match payment amount (RM ${paymentData.amount.toFixed(2)})`);
      return;
    }

    if (!receiptFile) {
      toast.error('Please upload receipt file');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('PaymentAmount', paymentData.amount.toString());
      formData.append('PaymentDate', paymentData.paymentDate);
      formData.append('PaymentMethod', paymentData.paymentMethod || 'Bank Transfer');
      formData.append('BankRefNo', paymentData.bankRefNo || '');
      formData.append('Notes', paymentData.notes || '');
      formData.append('ReceiptFile', receiptFile);

      // Add service order items
      selectedServiceOrders.forEach((so, index) => {
        formData.append(`ServiceOrderItems[${index}].ServiceOrderId`, so.id.toString());
        formData.append(`ServiceOrderItems[${index}].ServiceOrderNo`, so.serviceOrderNo);
        formData.append(`ServiceOrderItems[${index}].Amount`, so.allocatedAmount.toString());
      });

      await paymentVerificationAPI.submitPayment(formData);
      
      toast.success('Payment verification submitted successfully!');
      
      // Reset form
      setReceiptFile(null);
      setReceiptPreview(null);
      setOcrParsedData(null);
      setPaymentData({
        amount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        bankRefNo: '',
        notes: ''
      });
      setSelectedServiceOrders([]);
      setSearchQuery('');
      setSearchResults([]);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Submit Error:', error);
      toast.error(error.message || 'Failed to submit payment verification');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="p-4 space-y-4">
        {/* Upload Receipt Section */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Payment Receipt <span className="text-red-500">*</span>
          </label>
          
          {receiptPreview ? (
            <div className="relative mb-4">
              <img 
                src={receiptPreview} 
                alt="Receipt preview" 
                className="w-full rounded-lg border border-gray-300"
              />
              <button
                onClick={() => {
                  setReceiptFile(null);
                  setReceiptPreview(null);
                  setOcrParsedData(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                disabled={isSubmitting}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div
              onClick={() => !isSubmitting && fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleReceiptChange}
                className="hidden"
                disabled={isSubmitting}
              />
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm text-gray-600">Drag & Drop or Click to Upload Receipt</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, PDF (Max 5MB)</p>
            </div>
          )}

          {isProcessingOCR && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-gray-600">Processing receipt...</span>
            </div>
          )}
        </div>

        {/* Payment Details Section */}
        <div className="bg-white rounded-lg p-4 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Payment Details</h2>

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PAYMENT AMOUNT <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center border border-gray-300 rounded-md">
              <span className="px-3 py-2 bg-gray-100 text-gray-700 border-r border-gray-300">RM</span>
              <input
                type="number"
                step="0.01"
                value={paymentData.amount || ''}
                onChange={(e) => setPaymentData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
                className="flex-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PAYMENT DATE <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setOpenDatePicker(true)}
              disabled={isSubmitting}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-left bg-white flex items-center justify-between"
            >
              <span className={paymentData.paymentDate ? 'text-gray-900' : 'text-gray-400'}>
                {paymentData.paymentDate 
                  ? new Date(paymentData.paymentDate).toLocaleDateString('en-MY')
                  : 'Select Date'}
              </span>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <MobileDatePicker
              isOpen={openDatePicker}
              onClose={() => setOpenDatePicker(false)}
              selectedDate={paymentData.paymentDate ? new Date(paymentData.paymentDate) : null}
              onSelectDate={(date) => {
                if (date) {
                  const dateStr = date.toISOString().split('T')[0];
                  setPaymentData(prev => ({ ...prev, paymentDate: dateStr }));
                }
                setOpenDatePicker(false);
              }}
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <input
              type="text"
              value={paymentData.paymentMethod}
              onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
              placeholder="e.g. DuitNow, Bank Transfer"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* Bank Reference Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Reference Number
            </label>
            <input
              type="text"
              value={paymentData.bankRefNo}
              onChange={(e) => setPaymentData(prev => ({ ...prev, bankRefNo: e.target.value }))}
              placeholder="Enter bank reference number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NOTES (OPTIONAL)
            </label>
            <textarea
              value={paymentData.notes}
              onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Service Orders Section */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Service Orders</h2>
            <button
              onClick={() => setShowServiceOrderModal(true)}
              className="text-sm bg-primary text-white px-4 py-2 rounded-md"
              disabled={isSubmitting}
            >
              Add Service Order
            </button>
          </div>

          {selectedServiceOrders.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-500 text-sm">
              No service orders selected. Click "Add Service Order" to add.
            </div>
          ) : (
            <div className="space-y-3">
              {selectedServiceOrders.map((so) => (
                <div key={so.id} className="border border-gray-300 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{so.serviceOrderNo}</p>
                      <p className="text-sm text-gray-600">{so.customerName}</p>
                      <p className="text-xs text-gray-500">Balance: RM {so.remainingBalance.toFixed(2)}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveServiceOrder(so.id)}
                      className="text-red-500 ml-2"
                      disabled={isSubmitting}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Allocated:</span>
                    <div className="flex items-center border border-gray-300 rounded-md flex-1">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 border-r border-gray-300 text-sm">RM</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={so.allocatedAmount || ''}
                        onChange={(e) => handleUpdateAllocatedAmount(so.id, parseFloat(e.target.value) || 0)}
                        max={Math.min(so.remainingBalance, paymentData.amount - getTotalAllocated() + (so.allocatedAmount || 0))}
                        className="flex-1 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-700">Total Allocated:</span>
                  <span className="font-semibold text-gray-900">RM {getTotalAllocated().toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Remaining:</span>
                  <span className={`font-semibold ${getRemainingAmount() < 0 ? 'text-red-500' : 'text-gray-900'}`}>
                    RM {getRemainingAmount().toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmitPayment}
          disabled={isSubmitting || !paymentData.amount || !paymentData.paymentDate || selectedServiceOrders.length === 0 || !receiptFile}
          className={`w-full py-3 rounded-md text-white font-medium text-center ${
            isSubmitting || !paymentData.amount || !paymentData.paymentDate || selectedServiceOrders.length === 0 || !receiptFile
              ? 'bg-gray-400'
              : 'bg-primary hover:opacity-90'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          ) : (
            <span className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Submit Payment Proof
            </span>
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Our finance team will verify your payment within 24 hours.
        </p>
      </div>

      {/* Service Order Search Modal */}
      {showServiceOrderModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Search Service Orders</h2>
              <button 
                onClick={() => {
                  setShowServiceOrderModal(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="absolute right-4 top-4 text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchServiceOrders()}
                  placeholder="Search by Service Order No, Invoice No, or Customer Name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSearchServiceOrders}
                  disabled={isSearching || !searchQuery.trim()}
                  className="bg-primary text-white px-4 py-2 rounded-md disabled:bg-gray-400"
                >
                  {isSearching ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {searchResults.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {isSearching ? 'Searching...' : 'No results found. Try searching by Service Order No, Invoice No, or Customer Name.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((so) => (
                    <div
                      key={so.id}
                      onClick={() => {
                        if (!selectedServiceOrders.find(s => s.id === so.id)) {
                          handleAddServiceOrder(so);
                          setShowServiceOrderModal(false);
                        } else {
                          toast.info('Service order already added');
                        }
                      }}
                      className="border border-gray-300 rounded-lg p-3 cursor-pointer hover:border-primary transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{so.serviceOrderNo}</p>
                          {so.invoiceNo && <p className="text-sm text-gray-600">Invoice: {so.invoiceNo}</p>}
                          <p className="text-sm text-gray-600 mt-1">{so.customerName}</p>
                          {so.customerContactNo && <p className="text-xs text-gray-500">{so.customerContactNo}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-900">RM {so.totalAmount.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">Paid: RM {so.paidAmount.toFixed(2)}</p>
                          <p className="text-xs text-primary font-medium">Balance: RM {so.remainingBalance.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-center" autoClose={3000} />
    </div>
  );
};

export default PaymentVerification;
